(() => {
  const normalize = (value) => String(value || '').trim();
  const lower = (value) => normalize(value).toLowerCase();
  const emailOk = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalize(value));
  const maskEmail = (value) => {
    const [local, domain] = normalize(value).split('@');
    if (!local || !domain) return '***';
    return `${local.slice(0, 1)}***@${domain}`;
  };

  const initOneBot = (root) => {
    if (!root || root.dataset.onebotReady === 'true') return;
    root.dataset.onebotReady = 'true';

    // Move the actual widget to a direct child of <body>. This escapes any
    // Shopify section/header stacking context so the mobile panel can sit
    // above the fixed navigation reliably.
    if (document.body && root.parentElement !== document.body) {
      document.body.appendChild(root);
      root.dataset.onebotBodyPortal = 'true';
    }

    const launcher = root.querySelector('[data-onebot-launcher]');
    const panel = root.querySelector('[data-onebot-panel]');
    const closeBtn = root.querySelector('[data-onebot-close]');
    const conversation = root.querySelector('[data-onebot-conversation]');
    const form = root.querySelector('[data-onebot-form]');
    const input = root.querySelector('[data-onebot-input]');
    const topics = Array.from(root.querySelectorAll('[data-onebot-topic]'));
    const fallback = root.dataset.onebotFallback || 'Thanks for your question. Please choose one of the topics above.';
    const backend = normalize(root.dataset.onebotBackend);
    const returnsUrl = normalize(root.dataset.onebotReturnsUrl) || '/pages/returns';
    const state = { mode: null, orderNumber: '' };

    if (!launcher || !panel || !conversation || !form || !input) return;

    const scrollToBottom = () => requestAnimationFrame(() => { conversation.scrollTop = conversation.scrollHeight; });
    const setOpen = (open) => {
      root.classList.toggle('is-open', open);
      launcher.setAttribute('aria-expanded', open ? 'true' : 'false');
      panel.setAttribute('aria-hidden', open ? 'false' : 'true');
      if (open) { window.setTimeout(() => input.focus({ preventScroll: true }), 60); scrollToBottom(); }
      else launcher.focus({ preventScroll: true });
    };

    const addMessage = (text, type = 'bot') => {
      const row = document.createElement('div');
      row.className = `oss-onebot__message-row oss-onebot__message-row--${type}`;
      if (type === 'bot') {
        const avatar = document.createElement('div');
        avatar.className = 'oss-onebot__mini-avatar';
        avatar.setAttribute('aria-hidden', 'true');
        avatar.textContent = 'OS';
        row.appendChild(avatar);
      }
      const bubble = document.createElement('div');
      bubble.className = `oss-onebot__message oss-onebot__message--${type}`;
      bubble.textContent = text;
      row.appendChild(bubble);
      conversation.appendChild(row);
      scrollToBottom();
      return bubble;
    };

    const addLink = (label, href) => {
      const row = document.createElement('div');
      row.className = 'oss-onebot__message-row oss-onebot__message-row--bot';
      const avatar = document.createElement('div'); avatar.className = 'oss-onebot__mini-avatar'; avatar.textContent = 'OS'; avatar.setAttribute('aria-hidden','true');
      const bubble = document.createElement('div'); bubble.className = 'oss-onebot__message oss-onebot__message--bot';
      const a = document.createElement('a'); a.className = 'oss-onebot__inline-link'; a.href = href; a.textContent = label;
      bubble.appendChild(a); row.append(avatar, bubble); conversation.appendChild(row); scrollToBottom();
    };

    const addProducts = (products, query) => {
      const row = document.createElement('div'); row.className = 'oss-onebot__message-row oss-onebot__message-row--bot';
      const avatar = document.createElement('div'); avatar.className = 'oss-onebot__mini-avatar'; avatar.textContent = 'OS'; avatar.setAttribute('aria-hidden','true');
      const wrap = document.createElement('div'); wrap.className = 'oss-onebot__product-results';
      products.slice(0,4).forEach((product) => {
        const card = document.createElement('a'); card.className = 'oss-onebot__product-card'; card.href = product.url || `/products/${product.handle || ''}`;
        const imageUrl = product.image || product.featured_image?.url || product.featured_image || '';
        if (imageUrl) { const img = document.createElement('img'); img.src = imageUrl; img.alt = normalize(product.title); img.loading = 'lazy'; card.appendChild(img); }
        const body = document.createElement('span'); body.className = 'oss-onebot__product-card-body';
        const title = document.createElement('strong'); title.textContent = normalize(product.title); body.appendChild(title);
        const price = normalize(product.price || product.price_min || ''); if (price) { const p = document.createElement('span'); p.textContent = price; body.appendChild(p); }
        card.appendChild(body); wrap.appendChild(card);
      });
      const all = document.createElement('a'); all.className = 'oss-onebot__product-more'; all.href = `/search?q=${encodeURIComponent(query)}&type=product`; all.textContent = 'View all matching products →'; wrap.appendChild(all);
      row.append(avatar, wrap); conversation.appendChild(row); scrollToBottom();
    };

    const searchProducts = async (query) => {
      addMessage(`Searching the store for “${query}”…`);
      try {
        const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=4&resources[options][unavailable_products]=hide`;
        const response = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('search_failed');
        const data = await response.json();
        const products = data?.resources?.results?.products || [];
        if (products.length) {
          addMessage(`I found ${products.length} matching product${products.length === 1 ? '' : 's'}:`);
          addProducts(products, query);
        } else {
          addMessage(`I couldn't find an exact match for “${query}”.`);
          addLink('Search the full catalog →', `/search?q=${encodeURIComponent(query)}&type=product`);
        }
      } catch (_) {
        addMessage('The product search is temporarily unavailable. You can still search the full catalog here:');
        addLink('Search products →', `/search?q=${encodeURIComponent(query)}&type=product`);
      }
    };

    const backendRequest = async (action, orderNumber, email) => {
      if (!backend) {
        addMessage('Secure order lookup is not activated yet. No order data was accessed or exposed.');
        return;
      }
      try {
        const response = await fetch(backend, {
          method: 'POST', credentials: 'omit', cache: 'no-store',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ action, orderNumber, email })
        });
        if (!response.ok) throw new Error('backend_failed');
        const data = await response.json();
        if (data?.verified === false) { addMessage('I could not verify that order number and email address. Please check both entries and try again.'); return; }
        if (action === 'order_status') {
          const parts = [];
          if (data.status) parts.push(`Order status: ${data.status}`);
          if (data.fulfillmentStatus) parts.push(`Fulfillment: ${data.fulfillmentStatus}`);
          if (data.carrier) parts.push(`Carrier: ${data.carrier}`);
          if (data.trackingNumber) parts.push(`Tracking: ${data.trackingNumber}`);
          addMessage(parts.length ? parts.join('\n') : normalize(data.message) || 'Your order was verified, but no shipment update is available yet.');
          if (data.trackingUrl) addLink('Open tracking →', data.trackingUrl);
        } else {
          addMessage(normalize(data.message) || 'Your return request has been received for review.');
          if (data.returnUrl) addLink('Continue return →', data.returnUrl);
        }
      } catch (_) {
        addMessage('I could not securely reach the order service right now. Please try again later. No private order details were shown.');
      }
    };

    const startProductSearch = () => { state.mode = 'product_query'; input.placeholder = 'e.g. Liverpool third jersey'; addMessage('What product are you looking for? For example: “Liverpool third jersey” or “Nike Phantom”.'); };
    const startOrder = () => { state.mode = 'order_number'; state.orderNumber = ''; input.placeholder = 'Order number'; addMessage('Please enter your order number. I will ask for the email address used for the order before showing any status details.'); };
    const startReturns = () => { state.mode = 'return_order_number'; state.orderNumber = ''; input.placeholder = 'Order number'; addMessage('I can help with a return. First, please enter your order number. I will verify it with the email used for the order.'); addLink('Read the returns policy →', returnsUrl); };
    const reset = () => { state.mode = null; state.orderNumber = ''; input.placeholder = 'Ask me a question...'; };

    const inferredAction = (topic) => {
      const configured = lower(topic.dataset.onebotAction);
      const label = lower(topic.dataset.onebotLabel || topic.textContent);
      if (configured && configured !== 'faq') return configured;
      if (label.includes('order status')) return 'order_status';
      if (label.includes('returns') || label.includes('refund')) return 'returns';
      return configured || 'faq';
    };

    const answerTopic = (topic) => {
      const label = normalize(topic.dataset.onebotLabel || topic.textContent);
      if (label) addMessage(label, 'user');
      const action = inferredAction(topic);
      if (action === 'product_search') return startProductSearch();
      if (action === 'order_status') return startOrder();
      if (action === 'returns') return startReturns();
      addMessage(normalize(topic.dataset.onebotAnswer) || fallback);
    };

    const findFaq = (question) => topics.find((topic) => {
      if (inferredAction(topic) !== 'faq') return false;
      const label = lower(topic.dataset.onebotLabel);
      const keywords = normalize(topic.dataset.onebotKeywords).split(',').map(lower).filter(Boolean);
      const q = lower(question);
      return (label && q.includes(label)) || keywords.some((k) => k.length >= 2 && q.includes(k));
    }) || null;

    launcher.addEventListener('click', () => setOpen(!root.classList.contains('is-open')));
    closeBtn?.addEventListener('click', () => setOpen(false));
    topics.forEach((topic) => topic.addEventListener('click', () => answerTopic(topic)));

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const question = normalize(input.value); if (!question) return;
      input.value = '';

      if (state.mode === 'product_query') { addMessage(question, 'user'); reset(); await searchProducts(question); return; }
      if (state.mode === 'order_number' || state.mode === 'return_order_number') {
        addMessage(question, 'user'); state.orderNumber = question;
        state.mode = state.mode === 'order_number' ? 'order_email' : 'return_email'; input.placeholder = 'Email address'; addMessage('Now enter the email address used for this order.'); return;
      }
      if (state.mode === 'order_email' || state.mode === 'return_email') {
        if (!emailOk(question)) { addMessage('Please enter a valid email address.'); return; }
        addMessage(maskEmail(question), 'user');
        const action = state.mode === 'order_email' ? 'order_status' : 'return_request';
        const orderNumber = state.orderNumber; reset(); await backendRequest(action, orderNumber, question); return;
      }

      addMessage(question, 'user');
      const q = lower(question);
      if (/\b(find|search|looking for|show me|do you have)\b/.test(q)) { startProductSearch(); return; }
      if (/\b(order status|track my order|tracking|where is my order)\b/.test(q)) { startOrder(); return; }
      if (/\b(return|returns|refund|send back)\b/.test(q)) { startReturns(); return; }
      const faq = findFaq(question);
      addMessage(faq ? (normalize(faq.dataset.onebotAnswer) || fallback) : fallback);
    });

    document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && root.classList.contains('is-open')) setOpen(false); });
    panel.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      const focusable = Array.from(panel.querySelectorAll('button,input,a[href],[tabindex]:not([tabindex="-1"])')).filter((el) => !el.disabled && el.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });
  };

  const initAll = () => document.querySelectorAll('[data-onebot]').forEach(initOneBot);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll); else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
